import { supabase, Deuda, DeudaPago, DeudaConEstado } from '../lib/supabase';
import { OwnerContext, buildOwnerPayload } from '../types/actividad';
import { calcularEstadoDeuda, calcularValorCuota } from '../utils/deudaCalculations';
import { fechaEnChile } from '../utils/dateHelpers';
import type { RespaldoMovimiento } from '../types/comprobante';

interface CrearDeudaInput {
  owner: OwnerContext;
  acreedor: string;
  descripcion: string;
  monto_total: number;
  numero_cuotas: number;
  fecha_inicio_credito: string;
  mes_primer_vencimiento: string;
  dia_vencimiento: number;
  cuotasPreviasPagadas?: number; // historia previa: N primeras cuotas ya pagadas antes del sistema
  origen_deuda: 'compra_credito' | 'prestamo' | null;
  comprobante_origen_id: string | null;
  /** Solo préstamos depositados en la cuenta: lo que realmente llegó. */
  monto_recibido: number | null;
}

/** Columnas de entidad exclusivas de `deudas`, calcadas de `movimientos`. `global` no tiene
 * entidad propia (admin_global no gestiona deudas — solo lectura hacia abajo). */
const COLUMNAS_ENTIDAD_DEUDA: Partial<Record<OwnerContext['tipo'], keyof Pick<Deuda, 'lumisial_id' | 'diocesis_id' | 'provincia_eclesiastica_id'>>> = {
  lumisial: 'lumisial_id',
  diocesis: 'diocesis_id',
  provincia: 'provincia_eclesiastica_id',
};

export const deudaService = {
  /** Todas las deudas que el usuario puede ver (RLS filtra), con pagos y estado derivado. */
  async getVisibles(): Promise<DeudaConEstado[]> {
    const { data, error } = await supabase
      .from('deudas')
      .select(`
        *,
        lumisial:lumisiales(nombre),
        diocesis:diocesis(nombre),
        provincia:provincias_eclesiasticas(nombre),
        pagos:deuda_pagos(*, movimiento:movimientos(anulado)),
        comprobante_origen:comprobantes!deudas_comprobante_origen_id_fkey(id, tipo_archivo, rutas, nombre_original)
      `)
      .order('created_at', { ascending: false });
    if (error) throw error;

    // Supabase retorna joins anidados sin tipo genérico propio; el shape real difiere de
    // DeudaConEstado hasta normalizarlo acá (mismatch justificado, ver servicios.md).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data ?? []) as any[]).map((row: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pagos: DeudaPago[] = (row.pagos ?? []).map((p: any) => ({
        ...p, movimiento_anulado: p.movimiento?.anulado ?? null,
      }));
      const est = calcularEstadoDeuda(row, pagos);
      const entidadNombre = row.lumisial?.nombre ?? row.diocesis?.nombre ?? row.provincia?.nombre ?? '';
      return { ...row, pagos, entidadNombre, ...est };
    });
  },

  /** Guarda deuda, financiamiento e historia previa en una sola transacción. */
  async crear(input: CrearDeudaInput): Promise<string> {
    const owner = buildOwnerPayload(input.owner);
    const valorCuota = calcularValorCuota(input.monto_total, input.numero_cuotas);
    const { data: deudaId, error } = await supabase.rpc('crear_deuda_con_historia', {
      p_lumisial_id: owner.lumisial_id,
      p_diocesis_id: owner.diocesis_id,
      p_provincia_eclesiastica_id: owner.provincia_eclesiastica_id,
      p_acreedor: input.acreedor.trim(),
      p_descripcion: input.descripcion.trim(),
      p_monto_total: input.monto_total,
      p_numero_cuotas: input.numero_cuotas,
      p_valor_cuota: valorCuota,
      p_fecha_inicio_credito: input.fecha_inicio_credito,
      p_mes_primer_vencimiento: input.mes_primer_vencimiento,
      p_dia_vencimiento: input.dia_vencimiento,
      p_origen_deuda: input.origen_deuda,
      p_comprobante_origen_id: input.comprobante_origen_id,
      p_monto_recibido: input.monto_recibido,
      p_cuotas_previas_pagadas: input.cuotasPreviasPagadas ?? 0,
    });
    if (error) throw error;

    return deudaId as string;
  },

  /** `valor_cuota` se recalcula automáticamente cuando cambian monto_total y numero_cuotas juntos
   * (el modal siempre los envía como par) — nunca se acepta como input independiente. */
  async editar(id: string, campos: Partial<Pick<Deuda,
    'acreedor' | 'descripcion' | 'monto_total' | 'numero_cuotas' | 'fecha_inicio_credito' | 'mes_primer_vencimiento' | 'dia_vencimiento'>>): Promise<Deuda> {
    const update: Partial<Deuda> = { ...campos };
    if (campos.monto_total !== undefined && campos.numero_cuotas !== undefined) {
      update.valor_cuota = calcularValorCuota(campos.monto_total, campos.numero_cuotas);
    }
    const { data, error } = await supabase.from('deudas').update(update).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },

  /** Registra un pago (cuota completa o abono parcial) de forma atómica vía RPC, con su respaldo. */
  async registrarPago(
    deudaId: string, numeroCuota: number, fecha: string, monto: number, respaldo: RespaldoMovimiento,
  ): Promise<string> {
    const { data, error } = await supabase.rpc('registrar_pago_deuda', {
      p_deuda_id: deudaId,
      p_numero_cuota: numeroCuota,
      p_fecha: fecha,
      p_monto: monto,
      p_medio_pago: respaldo.medio_pago,
      p_folio_recibo: respaldo.folio_recibo,
      p_comprobante_id: respaldo.comprobante_id,
      p_comprobante_pendiente: respaldo.comprobante_pendiente_desde !== null,
    });
    if (error) throw error;
    return data as string;
  },

  /** Registra el saldo restante como un único pago que cierra la deuda, en la última cuota. */
  async saldar(deuda: DeudaConEstado, respaldo: RespaldoMovimiento): Promise<string> {
    if (deuda.estado !== 'activa') throw new Error('Esta deuda no tiene saldo pendiente');
    // El pago que cierra la deuda se fecha con el día civil de Chile; sin poder resolverlo, no se escribe.
    const hoy = fechaEnChile(new Date());
    if (!hoy) throw new Error('No pudimos determinar la fecha de hoy. Intenta nuevamente.');
    return this.registrarPago(deuda.id, deuda.numero_cuotas, hoy, deuda.saldo, respaldo);
  },

  /** La base valida el pago y su entidad; false en un replay también es éxito. */
  async anularPago(pago: DeudaPago, motivo: string): Promise<void> {
    const { error } = await supabase.rpc('anular_pago_deuda', {
      p_pago_id: pago.id, p_motivo: motivo,
    });
    if (error) throw error;
  },

  /** Anula la deuda y su entrada por financiamiento, si la tiene, en un solo paso. */
  async anularDeuda(id: string, motivo: string): Promise<void> {
    const { error } = await supabase.rpc('anular_deuda', { p_deuda_id: id, p_motivo: motivo });
    if (error) throw error;
  },

  /** Suma de saldos de deudas activas de UNA entidad (para la cifra del resumen de balance). */
  async getSaldoPendienteEntidad(owner: OwnerContext): Promise<number> {
    const col = COLUMNAS_ENTIDAD_DEUDA[owner.tipo];
    if (!col) throw new Error(`Tipo de entidad sin deudas propias: ${owner.tipo}`);
    const visibles = await this.getVisibles();
    return visibles
      .filter(d => d.estado === 'activa' && d[col] === owner.id)
      .reduce((s, d) => s + d.saldo, 0);
  },
};
